import React, { useState, useCallback } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  StatusBar,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';
import { useLeaseHistory } from '@/features/payments/hooks/useLeaseHistory';
import type { HistoryRow } from '@/features/payments/hooks/useLeaseHistory';
import { TransactionDetailsModal } from '@/features/payments/components/mobile/TransactionDetailsModal';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useSpacing } from '@/hooks/shared/useSpacing';
import type { RedesignThemeColors } from '@/constants/residents/theme';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { GradientBackground, PageHeader } from '@/components/residents/redesign';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { TransactionHistorySkeleton } from '@/components/common/skeleton';

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;
const ClockIcon = RESIDENTS_SVG_COMPONENTS.CLOCK;

const HP = REDESIGN_SPACING.horizontalPadding;

type TransactionStatus = 'received' | 'processing' | 'declined' | 'charge';

const getTransactionStatus = (row: HistoryRow): TransactionStatus => {
  if (row.type === 'payment') {
    if (row.status === 'pending') return 'processing';
    if (row.status === 'declined') return 'declined';
    return 'received';
  }
  return 'charge';
};

const getStatusLabel = (row: HistoryRow): string => {
  const status = getTransactionStatus(row);
  switch (status) {
    case 'processing': return 'Payment processing';
    case 'received': return 'Payment received';
    case 'declined': return 'Payment declined';
    default: return row.description;
  }
};

const getStatusIconConfig = (colors: RedesignThemeColors): Record<TransactionStatus, { bg: string; border: string; color: string }> => ({
  received: {
    bg: colors.accent.greenLight,
    border: colors.accent.greenLight,
    color: colors.accent.green,
  },
  processing: {
    bg: colors.status.inProgress,
    border: colors.status.inProgress,
    color: colors.status.inProgressText,
  },
  declined: {
    bg: colors.accent.redBg,
    border: colors.accent.redBg,
    color: colors.accent.red,
  },
  charge: {
    bg: colors.activityIcon.bg,
    border: colors.activityIcon.border,
    color: colors.text.dimmer,
  },
});

const TransactionIcon: React.FC<{ status: TransactionStatus }> = React.memo(({ status }) => {
  const { colors } = useThemeColors();
  const styles = useTransactionHistoryStyles();
  const config = getStatusIconConfig(colors)[status];
  return (
    <View style={[styles.statusIcon, { backgroundColor: config.bg, borderColor: config.border }]}>
      {status === 'processing' && ClockIcon ? (
        <ClockIcon width={18} height={18} color={config.color} />
      ) : CheckIcon ? (
        <CheckIcon width={18} height={18} color={config.color} />
      ) : null}
    </View>
  );
});

const TransactionRowItem: React.FC<{
  transaction: HistoryRow;
  onPress: (transaction: HistoryRow) => void;
}> = React.memo(({ transaction, onPress }) => {
  const styles = useTransactionHistoryStyles();
  const space = useSpacing();
  const status = getTransactionStatus(transaction);
  const label = getStatusLabel(transaction);
  const amount = formatCurrency(Math.abs(transaction.amount));
  const displayAmount = transaction.amount < 0 || transaction.type === 'payment' ? `-${amount}` : amount;

  return (
    <Hoverable
      hover="row"
      bleed={space.sm}
      style={styles.row}
      onPress={() => onPress(transaction)}
    >
      <TransactionIcon status={status} />
      <View style={styles.rowContent}>
        <Text style={styles.rowTitle} numberOfLines={2}>{label}</Text>
        <Text style={styles.rowDate}>{formatShortDate(transaction.date)}</Text>
      </View>
      <View style={styles.rowAmountContainer}>
        <Text style={styles.rowAmount}>{displayAmount}</Text>
        {transaction.paymentMethod && (
          <Text style={styles.rowPaymentMethod}>{transaction.paymentMethod}</Text>
        )}
      </View>
    </Hoverable>
  );
});

const ItemSeparator = () => {
  const styles = useTransactionHistoryStyles();
  return <View style={styles.separator} />;
};

const ListEmpty = () => {
  const styles = useTransactionHistoryStyles();
  return (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyText}>No transaction history available yet.</Text>
    </View>
  );
};

export const TransactionHistoryScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const styles = useTransactionHistoryStyles();
  const route = useRoute<any>();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  const { leaseId } = route.params as { leaseId: number };
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userId = authUser?.id;

  const { rows: transactions, isFetching } = useLeaseHistory(leaseId, userId);

  const [selectedTransaction, setSelectedTransaction] = useState<HistoryRow | null>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);

  const handleTransactionPress = useCallback((transaction: HistoryRow) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  }, []);

  const handleCloseTransactionDetails = useCallback(() => {
    setShowTransactionDetails(false);
    setSelectedTransaction(null);
  }, []);

  const handleGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const renderItem = useCallback(({ item }: { item: HistoryRow }) => (
    <TransactionRowItem transaction={item} onPress={handleTransactionPress} />
  ), [handleTransactionPress]);

  const keyExtractor = useCallback((item: HistoryRow) => item.id, []);

  const isInitialLoad = isFetching && transactions.length === 0;

  const listHeader = (
    <>
      <BackLink
        label="Back to Payments"
        onPress={handleGoBack}
        style={styles.backLink}
      />
      <PageHeader title="Transaction History" />
      {isInitialLoad && <TransactionHistorySkeleton variant="row" count={6} />}
    </>
  );

  const content = (
    <FlatList
      data={transactions}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      ItemSeparatorComponent={ItemSeparator}
      ListHeaderComponent={listHeader}
      ListEmptyComponent={!isInitialLoad ? ListEmpty : null}
      contentContainerStyle={[
        styles.listContent,
        { paddingBottom: isDesktop ? 60 : 40 + insets.bottom },
      ]}
      showsVerticalScrollIndicator={false}
    />
  );

  if (isDesktop) {
    return (
      <View style={styles.desktopContainer}>
        {content}
        <TransactionDetailsModal
          visible={showTransactionDetails}
          onClose={handleCloseTransactionDetails}
          transaction={selectedTransaction}
        />
      </View>
    );
  }

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen
        screenName="TransactionHistory"
        darkBackground
        headerVariant="gradient"
      >
        {content}
      </BaseResidentMobileScreen>
      <TransactionDetailsModal
        visible={showTransactionDetails}
        onClose={handleCloseTransactionDetails}
        transaction={selectedTransaction}
      />
    </GradientBackground>
  );
};

const useTransactionHistoryStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      desktopContainer: {
        flex: 1,
        backgroundColor: 'transparent',
      },
      listContent: {
        paddingHorizontal: HP,
      },
      backLink: {
        paddingTop: 16,
        paddingBottom: 4,
      },
      statusIcon: {
        width: 40,
        height: 40,
        borderRadius: 10,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
      },
      row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        paddingVertical: 16,
      },
      separator: {
        height: 1,
        backgroundColor: colors.separator,
      },
      rowContent: {
        flex: 1,
        gap: 2,
      },
      rowTitle: {
        fontSize: 14,
        fontWeight: '500',
        color: colors.text.tertiary,
      },
      rowDate: {
        ...REDESIGN_TYPOGRAPHY.caption,
        color: colors.text.dim,
      },
      rowAmountContainer: {
        alignItems: 'flex-end',
        gap: 2,
      },
      rowAmount: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.primary,
      },
      rowPaymentMethod: {
        fontSize: 11,
        fontWeight: '400',
        color: colors.text.dimmer,
        textAlign: 'right',
      },
      emptyContainer: {
        paddingVertical: 40,
        alignItems: 'center',
      },
      emptyText: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.muted,
        lineHeight: 21,
      },
    }),
  );
